import { Dispatch } from 'redux';
import cloneDeep from 'lodash/cloneDeep';
import { PaymentInfoDataSelector } from 'store/preRegistration/payment/selectors';
import { PaymentFormField } from 'store/preRegistration/payment/types';
import {
  PreRegistrationJwtTokenSelector,
  PreRegistrationMmiSelector,
  PreRegistrationEncounterInfoSelector,
  PreRegistrationVisitDetailSelector,
  PreRegistrationPatientGuarantorInfo,
  PreRegistrationPatientInfoSelector
} from 'store/preRegistration/selectors';

export const WEBPAY_AUTHENTICATE = 'WEBPAY.AUTHENTICATE';
export const WEBPAY_AUTHENTICATE_SUCCESS = 'WEBPAY.AUTHENTICATE_SUCCESS';
export const WEBPAY_AUTHENTICATE_FAIL = 'WEBPAY.AUTHENTICATE_FAIL';

export const WEBPAY_PAYMENTINFO = 'WEBPAY.PAYMENTINFO';
export const WEBPAY_PAYMENTINFO_SUCCESS = 'WEBPAY.PAYMENTINFO_SUCCESS';
export const WEBPAY_PAYMENTINFO_FAIL = 'WEBPAY.PAYMENTINFO_FAIL';
export const WEBPAY_PAYMENTINFO_RESET = 'WEBPAY.PAYMENTINFO_RESET';

export const WEBPAY_APPLYPAYMENT = 'WEBPAY.APPLYPAYMENT';
export const WEBPAY_APPLYPAYMENT_SUCCESS = 'WEBPAY.APPLYPAYMENT_SUCCESS';
export const WEBPAY_APPLYPAYMENT_FAIL = 'WEBPAY.APPLYPAYMENT_FAIL';

export const WEBPAY_ACCOUNT_SAVINGS = '37';
export const WEBPAY_ACCOUNT_CHECKING = '27';

export const authenticatePin = (webpayAuth: any) => ({
  type: WEBPAY_AUTHENTICATE,
  payload: {
    client: 'webpay',
    request: {
      method: 'post',
      url: '/public/authenticatePin',
      headers: {
        Accept: 'application/json',
        Connection: 'keep-alive',
        'Content-Type': 'application/json'
      },
      data: {
        fac: webpayAuth.fac,
        account: webpayAuth.account,
        pin: webpayAuth.pin
      }
    }
  }
});

export const paymentInfo = () => async (dispatch: Dispatch, getState: Function) => {
  const token = PreRegistrationJwtTokenSelector(getState());
  const mmi = PreRegistrationMmiSelector(getState());

  return dispatch({
    type: WEBPAY_PAYMENTINFO,
    payload: {
      client: 'webpay',
      request: {
        method: 'post',
        url: '/secure2/getPaymentInfo',
        headers: {
          Accept: 'application/json',
          Connection: 'keep-alive',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        data: {
          guarantor: {
            mmi
          }
        }
      }
    }
  });
};

export const resetPaymentInfo = () => ({
  type: WEBPAY_PAYMENTINFO_RESET
});

export const applyPayment = (
  form: PaymentFormField,
  priorBalance: boolean,
  payNow: boolean
) => async (dispatch: Dispatch, getState: Function) => {
  const token = PreRegistrationJwtTokenSelector(getState());
  const PaymentInfoData = PaymentInfoDataSelector(getState());
  const PatientInfoData = PreRegistrationPatientInfoSelector(getState());
  const EncounterInfoData = PreRegistrationEncounterInfoSelector(getState());
  const VisitDetailData = PreRegistrationVisitDetailSelector(getState());
  const GuarantorData = PreRegistrationPatientGuarantorInfo(getState());
  const data = cloneDeep(PaymentInfoData);

  if (payNow) {
    data.accountList.push({
      facilityId: VisitDetailData.facilityCode,
      accountNumber: `${VisitDetailData.finNumber}${
        VisitDetailData.patientTypeDescription === 'PreRecurring' ? '-n' : ''
      }`,
      alternateAccount: '',
      patientFirstName: PatientInfoData.firstName,
      patientLastName: PatientInfoData.lastName,
      patientName: `${PatientInfoData.lastName}, ${PatientInfoData.firstName}`,
      originatingARSystem: VisitDetailData.arType,
      centralTeam: EncounterInfoData.centralTeam,
      accountType: 'ARE',
      guarantorName: `${GuarantorData.lastName}, ${GuarantorData.firstName}`,
      guarantorReference: GuarantorData.id,
      enterpriseAccountId: EncounterInfoData.id,
      paymentAmount: VisitDetailData.payment.patientResponsibilityInCents
    });
  }

  if (priorBalance) {
    form.encounters.forEach((item: Record<string, any>, index: number) => {
      const { centralTeam } = data.accountList[index];
      // Adding amounts for SelfPay and VisitPay only, remaining all types are not cleared at insurance.
      if (centralTeam === 'S' || centralTeam === 'V') {
        data.accountList[index].paymentAmount = item.remainingAmount;
      }
    });
  }

  const totalPlanPaymentAmount = 0;
  const collectorApp = 'DFDMOBILE';

  let paymentOption = 'ACCOUNT';
  switch (form.paymentOption) {
    case '1':
      paymentOption = 'ACCOUNT';
      break;
    case '2':
      paymentOption = 'FULL';
      break;
    case '3':
      paymentOption = 'PLAN';
      break;
    case '4':
      paymentOption = 'OTHER';
      break;
    default:
      paymentOption = 'ACCOUNT';
      break;
  }

  let methodOption = '';
  switch (form.paymentMethod) {
    case '1':
      methodOption = 'CR';
      break;
    case '2':
      methodOption = 'CK';
      break;
    default:
      methodOption = 'CR';
      break;
  }

  data.paymentDetail = {
    collectorApp,
    paymentOption,
    totalPaymentAmount: form.paymentAmount,
    totalPlanPaymentAmount,
    methodOption,
    saveEmail: true,
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    address: form.address1 + form.address2,
    state: form.state,
    city: form.city,
    zip: form.zipCode,
    email: form.emailAddress,
    email2: form.emailAddress,
    scheduledPaymentDate: VisitDetailData.startDate
  };
  if (methodOption === 'CK') {
    data.paymentDetail.bankAccountType =
      form.accountType === '1' ? WEBPAY_ACCOUNT_SAVINGS : WEBPAY_ACCOUNT_CHECKING;
    data.paymentDetail.routingNumber = form.routingNumber;
    data.paymentDetail.accountNumber = form.accountNumber;
  } else if (methodOption === 'CR') {
    data.paymentDetail.cardNumber = form.cardNumber.replace(/\s/g, '');
    data.paymentDetail.expMonth = form.cardExpiration.substring(0, 2);
    data.paymentDetail.expYear = form.cardExpiration.substring(3, 5);
    data.paymentDetail.cvv2Code = form.cardCvv;
  }

  return dispatch({
    type: WEBPAY_APPLYPAYMENT,
    payload: {
      client: 'webpay',
      request: {
        method: 'post',
        url: '/secure2/applyPayment',
        headers: {
          Accept: 'application/json',
          Connection: 'keep-alive',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        data
      }
    }
  });
};
